/**
 * Leviathan - Lifting Detail Page
 * Quasar Prime LLC
 *
 * View lifting details with temperature correction and tax breakdown
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Edit,
  Loader2,
  Truck,
  Warehouse,
  Thermometer,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import AttachToShipments from '@/components/liftings/AttachToShipments';

interface LiftingItem {
  id: string;
  productId: string;
  product: { id: string; name: string; sku: string };
  grossGallons: number;
  netGallons: number;
  billedGallons: number;
  temperature: number | null;
  gravity: number | null;
  rackPrice: number;
  freightCost: number;
  otherCosts: number;
  totalCostPerGal: number;
  lineTotal: number;
  federalExciseTax: number;
  stateFuelTax: number;
  otherTax: number;
}

interface Lifting {
  id: string;
  liftingNumber: string;
  bolNumber: string;
  liftDate: string;
  status: string;
  receivedAt: string | null;
  receivedBy: string | null;
  totalGrossGallons: number;
  totalNetGallons: number;
  totalBilledGallons: number;
  totalAmount: number;
  supplierInvoiceNum: string | null;
  supplierInvoiceDate: string | null;
  carrierName: string | null;
  carrierFein: string | null;
  notes: string | null;
  createdAt: string;
  terminal: {
    id: string;
    name: string;
    tcn: string | null;
    city: string | null;
    state: string | null;
    operator: string | null;
  };
  supplier: { id: string; name: string } | null;
  truck: { id: string; unitNumber: string } | null;
  driver: { id: string; firstName: string; lastName: string } | null;
  receipt: { id: string; receiptNumber: string } | null;
  items: LiftingItem[];
}

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  EN_ROUTE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  AT_TERMINAL: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  LOADING: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  LOADED: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  DEPARTED: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50',
  COMPLETED: 'bg-green-500/20 text-green-400 border-green-500/50',
  CANCELLED: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
};

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  EN_ROUTE: 'En Route',
  AT_TERMINAL: 'At Terminal',
  LOADING: 'Loading',
  LOADED: 'Loaded',
  DEPARTED: 'Departed',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export default function LiftingDetail() {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: lifting, isLoading } = useQuery<Lifting>({
    queryKey: ['lifting', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals/liftings/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch lifting');
      const data = await res.json();
      return data.lifting;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      // POST, not PUT. The route is
      // `POST /api/terminals/liftings/:id/status` — a status transition, not a
      // replacement of the record. The old call had both the path and the
      // method wrong, so it could never have worked.
      const res = await fetch(`${API_URL}/api/terminals/liftings/${id}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifting', id] });
    },
  });

  const updateStatus = async (status: string) => {
    setIsUpdating(true);
    try {
      await updateStatusMutation.mutateAsync(status);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!lifting) {
    return (
      <div className="text-center py-12">
        <Truck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 mb-4">Lifting not found</p>
        <Link to="/liftings" className="text-amber-400 hover:text-amber-300">
          Back to Liftings
        </Link>
      </div>
    );
  }

  // Calculate temperature correction variance
  const tempVariance = lifting.totalGrossGallons - lifting.totalNetGallons;
  const tempVariancePercent = lifting.totalGrossGallons > 0
    ? ((tempVariance / lifting.totalGrossGallons) * 100).toFixed(2)
    : '0';

  // Calculate totals
  const totalRackCost = lifting.items.reduce((sum, i) => sum + (i.rackPrice * i.billedGallons), 0);
  const totalFreight = lifting.items.reduce((sum, i) => sum + (i.freightCost * i.billedGallons), 0);
  const totalOtherCosts = lifting.items.reduce((sum, i) => sum + (i.otherCosts * i.billedGallons), 0);
  const totalFederalTax = lifting.items.reduce((sum, i) => sum + i.federalExciseTax, 0);
  const totalStateTax = lifting.items.reduce((sum, i) => sum + i.stateFuelTax, 0);
  const totalOtherTax = lifting.items.reduce((sum, i) => sum + i.otherTax, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Link
              to="/liftings"
              className="text-slate-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-100">{lifting.liftingNumber}</h1>
            <span className={`px-3 py-1 text-sm border rounded ${STATUS_COLORS[lifting.status]}`}>
              {STATUS_LABELS[lifting.status] || lifting.status}
            </span>
          </div>
          <p className="text-amber-400 font-mono">
            Terminal BOL: {lifting.bolNumber}
          </p>
          <p className="text-slate-500 text-sm mt-1">
            {formatDate(lifting.liftDate)} from {lifting.terminal.name}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {lifting.status === 'SCHEDULED' && (
            <>
              <Link
                to={`/liftings/${id}/edit`}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Link>
              <button
                onClick={() => updateStatus('EN_ROUTE')}
                disabled={isUpdating}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Truck className="w-4 h-4" />
                Mark En Route
              </button>
            </>
          )}

          {lifting.status === 'EN_ROUTE' && (
            <button
              onClick={() => updateStatus('AT_TERMINAL')}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Warehouse className="w-4 h-4" />
              At Terminal
            </button>
          )}

          {lifting.status === 'AT_TERMINAL' && (
            <button
              onClick={() => updateStatus('LOADING')}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Clock className="w-4 h-4" />
              Start Loading
            </button>
          )}

          {lifting.status === 'LOADING' && (
            <button
              onClick={() => updateStatus('LOADED')}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Loaded
            </button>
          )}

          {lifting.status === 'LOADED' && (
            <button
              onClick={() => updateStatus('DEPARTED')}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Truck className="w-4 h-4" />
              Departed
            </button>
          )}

          {lifting.status === 'DEPARTED' && (
            <button
              onClick={() => updateStatus('COMPLETED')}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Complete
            </button>
          )}

          {lifting.status !== 'COMPLETED' && lifting.status !== 'CANCELLED' && (
            <button
              onClick={() => updateStatus('CANCELLED')}
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-red-600 text-slate-300 hover:text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard
          icon={<Truck className="w-5 h-5" />}
          label="Gross Gallons"
          value={lifting.totalGrossGallons.toLocaleString()}
        />
        <StatCard
          icon={<Thermometer className="w-5 h-5" />}
          label="Net Gallons (60°F)"
          value={lifting.totalNetGallons.toLocaleString()}
          highlight
        />
        <StatCard
          icon={<Thermometer className="w-5 h-5" />}
          label="Temp Correction"
          value={`${tempVariance >= 0 ? '-' : '+'}${Math.abs(tempVariance).toLocaleString()} (${tempVariancePercent}%)`}
          warning={Math.abs(parseFloat(tempVariancePercent)) > 1}
        />
        <StatCard
          icon={<FileText className="w-5 h-5" />}
          label="Billed Gallons"
          value={lifting.totalBilledGallons.toLocaleString()}
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5" />}
          label="Total Cost"
          value={formatCurrency(lifting.totalAmount)}
          highlight
        />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Line Items */}
        <div className="col-span-2 space-y-6">
          {/* Line Items */}
          <Panel title="Line Items">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-700">
                  <tr>
                    <th className="text-left py-3 text-slate-500 text-xs uppercase">Product</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Gross</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Temp</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Net (60°F)</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Cost/Gal</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lifting.items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-700/50">
                      <td className="py-3">
                        <div className="text-slate-100">{item.product.name}</div>
                        <div className="text-slate-500 text-xs">{item.product.sku}</div>
                      </td>
                      <td className="py-3 text-right text-slate-300">
                        {item.grossGallons.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-slate-400">
                        {item.temperature ? `${item.temperature}°F` : '-'}
                        {item.gravity && (
                          <div className="text-xs text-slate-500">API: {item.gravity}</div>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="text-amber-400 font-semibold">
                          {item.netGallons.toLocaleString()}
                        </div>
                        {item.grossGallons !== item.netGallons && (
                          <div className="text-xs text-slate-500">
                            {item.grossGallons > item.netGallons ? '-' : '+'}
                            {Math.abs(item.grossGallons - item.netGallons).toFixed(1)}
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="text-slate-100">
                          {formatCurrency(item.totalCostPerGal)}
                        </div>
                        <div className="text-xs text-slate-500">
                          Rack: {formatCurrency(item.rackPrice)}
                        </div>
                      </td>
                      <td className="py-3 text-right text-amber-400 font-semibold">
                        {formatCurrency(item.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-amber-500">
                    <td colSpan={3} className="py-3 text-right text-slate-400">Totals</td>
                    <td className="py-3 text-right text-amber-400 font-bold">
                      {lifting.totalNetGallons.toLocaleString()}
                    </td>
                    <td></td>
                    <td className="py-3 text-right text-amber-400 font-bold text-lg">
                      {formatCurrency(lifting.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>

          {/* Taxes */}
          {(totalFederalTax > 0 || totalStateTax > 0 || totalOtherTax > 0) && (
            <Panel title="Taxes Paid at Terminal">
              <table className="w-full">
                <thead className="border-b border-slate-700">
                  <tr>
                    <th className="text-left py-3 text-slate-500 text-xs uppercase">Product</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Federal Excise</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">State Fuel Tax</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Other Tax</th>
                    <th className="text-right py-3 text-slate-500 text-xs uppercase">Total Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {lifting.items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-700/50">
                      <td className="py-2 text-slate-100 text-sm">{item.product.name}</td>
                      <td className="py-2 text-right text-slate-400 text-sm">
                        {formatCurrency(item.federalExciseTax)}
                      </td>
                      <td className="py-2 text-right text-slate-400 text-sm">
                        {formatCurrency(item.stateFuelTax)}
                      </td>
                      <td className="py-2 text-right text-slate-400 text-sm">
                        {formatCurrency(item.otherTax)}
                      </td>
                      <td className="py-2 text-right text-amber-400 text-sm">
                        {formatCurrency(item.federalExciseTax + item.stateFuelTax + item.otherTax)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-600">
                    <td className="py-2 text-slate-400 font-medium">Totals</td>
                    <td className="py-2 text-right text-slate-300">{formatCurrency(totalFederalTax)}</td>
                    <td className="py-2 text-right text-slate-300">{formatCurrency(totalStateTax)}</td>
                    <td className="py-2 text-right text-slate-300">{formatCurrency(totalOtherTax)}</td>
                    <td className="py-2 text-right text-amber-400 font-bold">
                      {formatCurrency(totalFederalTax + totalStateTax + totalOtherTax)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Panel>
          )}

          {/* Cost Breakdown */}
          <Panel title="Cost Breakdown">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div className="text-slate-500 text-xs uppercase mb-2">Rack Cost</div>
                <div className="text-slate-100 text-lg">{formatCurrency(totalRackCost)}</div>
              </div>
              <div className="text-center p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div className="text-slate-500 text-xs uppercase mb-2">Freight</div>
                <div className="text-slate-100 text-lg">{formatCurrency(totalFreight)}</div>
              </div>
              <div className="text-center p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div className="text-slate-500 text-xs uppercase mb-2">Other Costs</div>
                <div className="text-slate-100 text-lg">{formatCurrency(totalOtherCosts)}</div>
              </div>
              <div className="text-center p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg">
                <div className="text-amber-400 text-xs uppercase mb-2">Total</div>
                <div className="text-amber-400 text-lg font-bold">{formatCurrency(lifting.totalAmount)}</div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <div className="text-slate-500 text-xs">
                Average Cost per Gallon:{' '}
                <span className="text-amber-400 font-semibold">
                  {formatCurrency(lifting.totalBilledGallons > 0 ? lifting.totalAmount / lifting.totalBilledGallons : 0)}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* Right Column - Details */}
        <div className="space-y-6">
          {/* Terminal Info */}
          <Panel title="Terminal">
            <div className="space-y-3">
              <Link
                to={`/terminals/${lifting.terminal.id}`}
                className="text-amber-400 hover:text-amber-300 font-semibold block"
              >
                {lifting.terminal.name}
              </Link>
              {lifting.terminal.tcn && (
                <div className="text-slate-400 text-sm font-mono">{lifting.terminal.tcn}</div>
              )}
              {(lifting.terminal.city || lifting.terminal.state) && (
                <div className="text-slate-500 text-sm">
                  {lifting.terminal.city}, {lifting.terminal.state}
                </div>
              )}
              {lifting.terminal.operator && (
                <div className="text-slate-500 text-sm">
                  Operator: {lifting.terminal.operator}
                </div>
              )}
            </div>
          </Panel>

          {/* Supplier Info */}
          {lifting.supplier && (
            <Panel title="Supplier">
              <div className="space-y-3">
                <div className="text-slate-100">{lifting.supplier.name}</div>
                {lifting.supplierInvoiceNum && (
                  <InfoRow label="Invoice #" value={lifting.supplierInvoiceNum} />
                )}
                {lifting.supplierInvoiceDate && (
                  <InfoRow label="Invoice Date" value={formatDate(lifting.supplierInvoiceDate)} />
                )}
              </div>
            </Panel>
          )}

          {/* Carrier Info */}
          {(lifting.truck || lifting.driver || lifting.carrierName) && (
            <Panel title="Carrier">
              <div className="space-y-3">
                {lifting.truck && (
                  <InfoRow label="Truck" value={lifting.truck.unitNumber} />
                )}
                {lifting.driver && (
                  <InfoRow label="Driver" value={`${lifting.driver.firstName} ${lifting.driver.lastName}`} />
                )}
                {lifting.carrierName && (
                  <InfoRow label="Carrier" value={lifting.carrierName} />
                )}
                {lifting.carrierFein && (
                  <InfoRow label="Carrier FEIN" value={lifting.carrierFein} />
                )}
              </div>
            </Panel>
          )}

          {/* Timeline */}
          <Panel title="Timeline">
            <div className="space-y-4">
              <TimelineItem
                label="Created"
                date={formatDateTime(lifting.createdAt)}
                completed
              />
              {lifting.status !== 'SCHEDULED' && lifting.status !== 'CANCELLED' && (
                <TimelineItem
                  label="In Progress"
                  date="Started"
                  completed={['COMPLETED'].includes(lifting.status)}
                  active={!['COMPLETED', 'CANCELLED'].includes(lifting.status)}
                />
              )}
              {lifting.receivedAt && (
                <TimelineItem
                  label="Received"
                  date={formatDateTime(lifting.receivedAt)}
                  completed
                />
              )}
              {lifting.status === 'COMPLETED' && (
                <TimelineItem
                  label="Completed"
                  date="Done"
                  completed
                />
              )}
              {lifting.status === 'CANCELLED' && (
                <TimelineItem
                  label="Cancelled"
                  date="-"
                  cancelled
                />
              )}
            </div>
          </Panel>

          {/* Linked Receipt */}
          {lifting.receipt && (
            <Panel title="Linked Receipt">
              <Link
                to={`/receipts/${lifting.receipt.id}`}
                className="text-amber-400 hover:text-amber-300"
              >
                {lifting.receipt.receiptNumber} &rarr;
              </Link>
            </Panel>
          )}

          {/* Notes */}
          {lifting.notes && (
            <Panel title="Notes">
              <div className="text-slate-400 text-sm whitespace-pre-wrap">
                {lifting.notes}
              </div>
            </Panel>
          )}
        </div>
      </div>

      {/*
        Attaching this lifting to the shipments it supplied.

        Full width and below the detail, because it is a working screen rather
        than a field: liftings are usually keyed in AFTER the shipments they
        supplied, so this is where origin actually gets recorded most of the
        time. Nothing is attached without confirmation — see the component.
      */}
      <AttachToShipments
        liftingId={id!}
        onAttached={() => queryClient.invalidateQueries({ queryKey: ['lifting', id] })}
      />
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  highlight,
  warning
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
  warning?: boolean;
}) {
  return (
    <div className={`relative bg-slate-800/50 border rounded-xl p-4 ${
      highlight ? 'border-amber-500/50' : warning ? 'border-yellow-500/50' : 'border-slate-700/50'
    }`}>
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber-500/50 rounded-tl-xl" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber-500/50 rounded-tr-xl" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber-500/50 rounded-bl-xl" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber-500/50 rounded-br-xl" />

      <div className="flex items-center gap-2 text-slate-500 mb-1">
        {icon}
        <span className="text-xs uppercase">{label}</span>
      </div>
      <div className={`text-xl font-bold ${
        highlight ? 'text-amber-400' : warning ? 'text-yellow-400' : 'text-slate-100'
      }`}>
        {value}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500/50 rounded-tl-xl" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500/50 rounded-tr-xl" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500/50 rounded-bl-xl" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500/50 rounded-br-xl" />

      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-100 uppercase tracking-wide mb-4 pb-3 border-b border-amber-500/30">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-slate-500 text-xs uppercase mb-1">{label}</div>
      <div className="text-slate-100">{value}</div>
    </div>
  );
}

function TimelineItem({
  label,
  date,
  completed,
  active,
  cancelled
}: {
  label: string;
  date: string;
  completed?: boolean;
  active?: boolean;
  cancelled?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-3 h-3 rounded-full mt-1 ${
        cancelled ? 'bg-red-500' :
        completed ? 'bg-green-500' :
        active ? 'bg-amber-500 animate-pulse' :
        'bg-slate-600'
      }`} />
      <div>
        <div className={`${
          cancelled ? 'text-red-400' :
          completed ? 'text-green-400' :
          active ? 'text-amber-400' :
          'text-slate-400'
        }`}>
          {label}
        </div>
        <div className="text-slate-500 text-xs">{date}</div>
      </div>
    </div>
  );
}
